import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, ListTree, FolderOpen, MessageSquare, Settings, LogOut, Languages, Users, ShieldCheck, ScrollText, BarChart3, Inbox, Scale, HelpCircle, Wrench } from "lucide-react";

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useLang();
  const nav = useNavigate();
  const role = user?.role || "admin";
  const isAdmin = role === "admin";
  const isResearcher = role === "researcher";
  const links = [
    { to: "/admin", end: true, icon: LayoutDashboard, label: t("admin.dashboard"), tid: "admin-nav-dashboard", show: true },
    { to: "/admin/instruments", icon: ListTree, label: t("admin.instruments"), tid: "admin-nav-instruments", show: !isResearcher },
    { to: "/admin/categories", icon: FolderOpen, label: t("admin.categories"), tid: "admin-nav-categories", show: !isResearcher },
    { to: "/admin/responses", icon: MessageSquare, label: t("admin.responses"), tid: "admin-nav-responses", show: true },
    { to: "/admin/analytics", icon: BarChart3, label: lang === "id" ? "Analitik" : "Analytics", tid: "admin-nav-analytics", show: true },
    { to: "/admin/public-users", icon: Users, label: lang === "id" ? "Pengguna" : "Users", tid: "admin-nav-public-users", show: isAdmin },
    { to: "/admin/users", icon: Users, label: lang === "id" ? "Akun Staff" : "Staff Accounts", tid: "admin-nav-users", show: isAdmin },
    { to: "/admin/audit", icon: ScrollText, label: lang === "id" ? "Audit" : "Audit Log", tid: "admin-nav-audit", show: isAdmin },
    { to: "/admin/feedback", icon: Inbox, label: lang === "id" ? "Umpan Balik" : "Feedback", tid: "admin-nav-feedback", show: isAdmin },
    { to: "/admin/legal", icon: Scale, label: lang === "id" ? "Legal" : "Legal", tid: "admin-nav-legal", show: isAdmin },
    { to: "/admin/faq", icon: HelpCircle, label: "FAQ", tid: "admin-nav-faq", show: isAdmin },
    { to: "/admin/maintenance", icon: Wrench, label: lang === "id" ? "Pemeliharaan" : "Maintenance", tid: "admin-nav-maintenance", show: isAdmin },
    { to: "/admin/settings", icon: Settings, label: t("admin.settings"), tid: "admin-nav-settings", show: true },
    { to: "/admin/2fa", icon: ShieldCheck, label: "2FA", tid: "admin-nav-2fa", show: true },
  ];
  const doLogout = async () => { await logout(); nav("/login"); };
  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-secondary/40">
      <aside className="w-full md:w-64 shrink-0 border-b md:border-b-0 md:border-r border-border bg-card flex flex-col">
        <div className="px-5 py-6 border-b border-border">
          <div className="font-display font-semibold text-lg tracking-tight">Ruang Refleksi</div>
          <div className="text-xs text-muted-foreground capitalize">{role}</div>
        </div>
        <nav className="flex md:block flex-1 p-3 gap-1 md:space-y-1 overflow-x-auto md:overflow-y-auto">
          {links.filter(l => l.show).map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={l.tid}
              className={({isActive}) =>
                `shrink-0 flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive ? "bg-primary/10 text-primary font-medium" : "text-foreground/70 hover:bg-secondary hover:text-foreground"
                }`
              }
            >
              <l.icon className="w-4 h-4" />
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-border space-y-2">
          <div className="text-xs text-muted-foreground px-2 truncate">{user?.username || user?.name}</div>
          <div className="flex items-center gap-2">
            <button data-testid="admin-lang-toggle" onClick={() => setLang(lang === "id" ? "en" : "id")} className="flex-1 flex items-center gap-1 justify-center px-3 py-1.5 rounded-md border border-border text-xs uppercase hover:bg-secondary">
              <Languages className="w-3.5 h-3.5" /> {lang === "id" ? "ID" : "EN"}
            </button>
            <Button data-testid="admin-logout-btn" size="sm" variant="outline" onClick={doLogout} className="gap-1">
              <LogOut className="w-3.5 h-3.5" /> {t("admin.logout")}
            </Button>
          </div>
        </div>
      </aside>
      <main className="flex-1 min-w-0 overflow-x-hidden">
        <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
          {children}
        </div>
      </main>
    </div>
  );
}
